import { useEffect, useCallback } from 'react';
import { useEditorStore } from './store/editor-store';
import { parseSource } from './lib/parser';
import { sanitizeHtml } from './lib/sanitizer';
import { useDebounce } from './hooks/useDebounce';
import { useDragDrop } from './hooks/useDragDrop';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toolbar } from './components/Toolbar';
import { EditorPane } from './components/EditorPane';
import { PreviewPane } from './components/PreviewPane';
import { SplitLayout } from './components/SplitLayout';
import { StatusBar } from './components/StatusBar';

function AppContent() {
  const { source, renderedHtml, liveSync, viewMode, setSource, setRenderedHtml } = useEditorStore();
  const debouncedSource = useDebounce(source, 100);

  const handleFileDrop = useCallback(
    (content: string) => {
      setSource(content);
    },
    [setSource]
  );

  const { isDragging, dragProps } = useDragDrop({ onFileDrop: handleFileDrop });

  useEffect(() => {
    if (!liveSync) return;

    const processSource = async () => {
      const result = await parseSource(debouncedSource);
      const sanitized = sanitizeHtml(result.html);
      setRenderedHtml(sanitized);
      useEditorStore.setState({ detectedFormat: result.format });
    };

    processSource();
  }, [debouncedSource, liveSync, setRenderedHtml]);

  const detectedFormat = useEditorStore((s) => s.detectedFormat);

  return (
    <div
      {...dragProps}
      className={`h-screen flex flex-col bg-[#f5f5f7] pt-2 ${
        isDragging ? 'ring-4 ring-inset ring-blue-500' : ''
      }`}
    >
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90 pointer-events-none">
          <div className="text-2xl font-semibold text-blue-600">Drop file to load</div>
        </div>
      )}
      <Toolbar />
      <div className="flex-1 overflow-hidden mx-10 my-5 rounded-xl shadow-xl border border-gray-200 bg-white">
        <SplitLayout
          left={<EditorPane format={detectedFormat} />}
          right={<PreviewPane html={renderedHtml} viewMode={viewMode} />}
        />
      </div>
      <StatusBar source={source} format={detectedFormat} />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}
